import * as React from "react";
import { Source, Layer } from "react-map-gl/mapbox";
import {
  getGeofenceFillPaint,
  getGeofenceStrokePaint,
} from "./geofenceLayerPaint";

const COUNT_LAYOUT = {
  "text-field": [
    "case",
    [">", ["get", "device_count"], 0],
    ["to-string", ["get", "device_count"]],
    "",
  ],
  "text-size": 11,
  "text-allow-overlap": true,
  "text-ignore-placement": true,
  "text-font": ["DIN Offc Pro Bold", "Arial Unicode MS Bold"],
};

const LABEL_LAYOUT = {
  "text-field": ["get", "geofence_name"],
  "text-size": 11,
  "text-anchor": "top",
  "text-offset": [0, 1.4],
  "text-max-width": 10,
  "text-allow-overlap": false,
  "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Regular"],
};

/**
 * Geofence overlay rendered beneath device markers on the devices map:
 * polygon fill/outline plus centre badges showing the device-count, and name
 * labels. Paint/layout objects are memoised to avoid per-render churn.
 *
 * @param {Object} props
 * @param {Object} props.geofenceSource - Polygon GeoJSON source
 * @param {Object} props.geofencePinSource - Centre-pin GeoJSON source
 * @param {string} props.primaryColor - Theme primary colour (badge fill)
 * @param {string} props.themeMode - MUI palette mode ("light" | "dark")
 */
const GeofenceOverlayLayers = ({
  geofenceSource,
  geofencePinSource,
  primaryColor,
  themeMode,
}) => {
  const fillPaint = React.useMemo(
    () => getGeofenceFillPaint(themeMode),
    [themeMode]
  );
  const strokePaint = React.useMemo(
    () => getGeofenceStrokePaint(themeMode),
    [themeMode]
  );

  const pinPaint = React.useMemo(
    () => ({
      "circle-radius": 7,
      "circle-color": [
        "case",
        [">", ["get", "device_count"], 0],
        primaryColor,
        ["==", ["get", "geofence_status"], "active"],
        "#4CAF50",
        "#9E9E9E",
      ],
      "circle-opacity": 0.95,
      "circle-stroke-width": 2,
      "circle-stroke-color": "#ffffff",
    }),
    [primaryColor]
  );

  const labelPaint = React.useMemo(
    () => ({
      "text-color": themeMode === "dark" ? "#e0e0e0" : "#212121",
      "text-halo-color":
        themeMode === "dark" ? "rgba(0,0,0,0.9)" : "rgba(255,255,255,0.92)",
      "text-halo-width": 1.5,
    }),
    [themeMode]
  );

  return (
    <>
      {/* Geofence polygon source + fill/outline */}
      <Source id="geofences" type="geojson" data={geofenceSource} />
      <Source id="geofence-pins" type="geojson" data={geofencePinSource} />

      <Layer
        id="geofence-fill"
        type="fill"
        source="geofences"
        paint={fillPaint}
      />
      <Layer
        id="geofence-outline"
        type="line"
        source="geofences"
        paint={strokePaint}
      />

      {/* Geofence badge circles */}
      <Layer
        id="geofence-pins"
        type="circle"
        source="geofence-pins"
        paint={pinPaint}
      />

      {/* Device count inside badge */}
      <Layer
        id="geofence-count"
        type="symbol"
        source="geofence-pins"
        layout={COUNT_LAYOUT}
        paint={{ "text-color": "#ffffff" }}
      />

      {/* Geofence name labels */}
      <Layer
        id="geofence-labels"
        type="symbol"
        source="geofence-pins"
        minzoom={11}
        layout={LABEL_LAYOUT}
        paint={labelPaint}
      />
    </>
  );
};

export default React.memo(GeofenceOverlayLayers);
